import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson } from '@ourbranch/lookups';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { useStore } from 'core/store';
import { propDetailsCodes } from '../quote-forms/quote-forms.constants';
import { BarndomininumDwellingForm } from 'common/components/farm/barndominium-dwelling-form';
import { MobileHomeForm } from 'common/components/farm/mobile-home-form';

import useStyles from './initial-risk-detail.styles';

// Form options constants
const nfidOptions = [
  { id: '1', value: '1' },
  { id: '2', value: '2' },
  { id: '3', value: '3' },
  { id: '4', value: '4' }
];
const ownershipStatusOptions = lookupsJson.farm.ownershipStatus;
const tohOptions = lookupsJson.typeOfHome;
const hqOptions = lookupsJson.homeQuality;
const ctOptions = lookupsJson.farm.dwellingConstructionType;
const btOptions = lookupsJson.basementType;
const ewtOptions = lookupsJson.exteriorWallType;
const gtOptions = lookupsJson.garageType;
const roOptions = lookupsJson.roofOriginal;
const rtOptions = lookupsJson.farm.dwellingRoofType;
const rsOptions = lookupsJson.roofShape;
const ruOptions = lookupsJson.farm.residenceUsage.filter((option) => option.id !== 'C');
const propertyTypeOptions = lookupsJson.farm.propertyType;
const heatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;

const FO3RiskDetail = () => {
  const classes = useStyles();
  const {
    quote: { getAlerts }
  } = useStore();

  const { values, isSubmitting, setFieldValue } = useFormikContext();
  const alerts = getAlerts(propDetailsCodes, values.policyType);
  const needMoreData = alerts.some((alert) => [5004, 50006].includes(alert.code));
  const initialRiskDetailErrors = useRef(null);
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;
  const buildingPath = `${farmRiskPath}.building`;
  const riskAddressPath = `${farmRiskPath}.address`;

  const toggleAdditionalFields = useCallback(() => {
    setShowAdditionalFields(!showAdditionalFields);
  }, [showAdditionalFields]);

  useEffect(() => {
    if (alerts.length && initialRiskDetailErrors.current && isSubmitting) {
      initialRiskDetailErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (needMoreData) {
      setShowAdditionalFields(true);
    } else {
      setShowAdditionalFields(false);
    }
  }, [needMoreData]);

  useEffect(() => {
    const useApplicantAddress = farmRisk?.useApplicantAddress;
    if (useApplicantAddress && values.address) {
      setFieldValue(`${riskAddressPath}.address`, values.address);
      setFieldValue(`${riskAddressPath}.city`, values.city);
      setFieldValue(`${riskAddressPath}.state`, values.state);
      setFieldValue(`${riskAddressPath}.zip`, values.zip);
      // County is set in the primary location details component
      setFieldValue(`${riskAddressPath}.county`, farmLocation?.address?.county);
    } else {
      setFieldValue(`${riskAddressPath}.address`, undefined);
      setFieldValue(`${riskAddressPath}.city`, undefined);
      setFieldValue(`${riskAddressPath}.state`, undefined);
      setFieldValue(`${riskAddressPath}.zip`, undefined);
      setFieldValue(`${riskAddressPath}.county`, undefined);
    }
  }, [
    farmRisk?.useApplicantAddress,
    values.address,
    values.city,
    values.state,
    values.zip,
    farmLocation?.address?.county,
    setFieldValue,
    riskAddressPath
  ]);

  const isSelectedHeatingSourceUnitCountBased = heatingSourceOptions.find(
    (option) => option.id === farmRisk?.building?.primaryHeatingSource
  )?.unitCountBased;

  const isSelectedSecondaryHeatingSourceUnitCountBased = heatingSourceOptions.find(
    (option) => option.id === farmRisk?.building?.secondaryHeatingSource
  )?.unitCountBased;

  // Check if structure category is Barndominium or Mobile Home
  const isBarndominium = farmRisk?.building?.propertyType === 'B';
  const isMobileHome = farmRisk?.building?.propertyType === 'MH';

  return (
    <Grid container spacing={2}>
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.propertyType"
        name={`${buildingPath}.propertyType`}
        mode="light"
        label="Structure Category*"
        options={propertyTypeOptions}
      />
      <Field
        xs={6}
        type="select"
        id="farmRisk.building.residenceUsage"
        name={`${buildingPath}.residenceUsage`}
        mode="light"
        label="Residence Type/Usage*"
        options={ruOptions}
      />

      {needMoreData && !isBarndominium && !isMobileHome && (
        <Grid item xs={12} className={classes.checkboxContainer}>
          <Label type="action" onClick={toggleAdditionalFields}>
            {showAdditionalFields ? 'Hide additional fields' : 'Show additional fields'}
          </Label>
        </Grid>
      )}

      {isBarndominium && (
        <BarndomininumDwellingForm
          basePath={farmRiskPath}
          isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
          isSelectedSecondaryHeatingSourceUnitCountBased={isSelectedSecondaryHeatingSourceUnitCountBased}
          state={values.state}
        />
      )}

      {isMobileHome && (
        <MobileHomeForm
          basePath={farmRiskPath}
          isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
          isSelectedSecondaryHeatingSourceUnitCountBased={isSelectedSecondaryHeatingSourceUnitCountBased}
          state={values.state}
        />
      )}
      {showAdditionalFields && (
        <>
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.yearBuilt"
            name={`${buildingPath}.yearBuilt`}
            mode="light"
            label="Year Built"
          />
          <Field
            xs={4}
            type="select"
            options={tohOptions}
            id="farmRisk.building.typeOfHome"
            name={`${buildingPath}.typeOfHome`}
            mode="light"
            label="Type of home"
          />
          <Field
            name={`${buildingPath}.ownershipStatus`}
            type="select"
            label="Ownership"
            mode="light"
            options={ownershipStatusOptions}
            xs={4}
          />
          <Field
            xs={3}
            type="select"
            options={nfidOptions}
            id="farmRisk.building.numFamiliesInDwelling"
            name={`${buildingPath}.numFamiliesInDwelling`}
            mode="light"
            label="Families living"
          />
          <Field
            xs={3}
            type="select"
            options={hqOptions}
            id="farmRisk.building.homeQuality"
            name={`${buildingPath}.homeQuality`}
            mode="light"
            label="Home quality"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.sqFt"
            name={`${buildingPath}.sqFt`}
            mode="light"
            label="Above-ground sqft"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.constructionType"
            options={ctOptions}
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Ext. wall construction"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numStories"
            name={`${buildingPath}.numStories`}
            mode="light"
            label="Stories"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numHalfBathrooms"
            name={`${buildingPath}.numHalfBathrooms`}
            mode="light"
            label="Half Bathrooms"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numFullBathrooms"
            name={`${buildingPath}.numFullBathrooms`}
            mode="light"
            label="Full Bathrooms"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.numFireplaces"
            name={`${buildingPath}.numFireplaces`}
            mode="light"
            label="Fireplaces"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.numWoodBurningStoves"
            name={`${buildingPath}.numWoodBurningStoves`}
            mode="light"
            label="Wood Burning Stoves"
          />
          <Field
            xs={4}
            type="select"
            id="farmRisk.building.basementType"
            options={btOptions}
            name={`${buildingPath}.basementType`}
            mode="light"
            label="Basement/Foundation type"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.basementSqFt"
            name={`${buildingPath}.basementSqFt`}
            mode="light"
            label="Basement finished sqft"
          />
          <Field
            xs={6}
            type="select"
            id="farmRisk.building.exteriorWallType"
            options={ewtOptions}
            name={`${buildingPath}.exteriorWallType`}
            mode="light"
            label="Exterior wall finish"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.garageType"
            options={gtOptions}
            name={`${buildingPath}.garageType`}
            mode="light"
            label="Type of Garage"
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.garageCarCapacity"
            name={`${buildingPath}.garageCarCapacity`}
            mode="light"
            label="Garage's Car space"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.primaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.primaryHeatingSource`}
            mode="light"
            label="Primary Heating"
          />
          <Field
            xs={3}
            type="numeric"
            id={'farmRisk.building.numHeatingSources'}
            name={'farmRisk.building.numHeatingSources'}
            mode="light"
            label="Heating units"
            disabled={!isSelectedHeatingSourceUnitCountBased}
            helperText="Acceptable range: 1-1000"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.secondaryHeatingSource"
            options={heatingSourceOptions}
            name={`${buildingPath}.secondaryHeatingSource`}
            mode="light"
            label="Secondary Heating"
          />
          <Field
            xs={3}
            type="numeric"
            id={'farmRisk.building.numSecondaryHeatingSources'}
            name={'farmRisk.building.numSecondaryHeatingSources'}
            mode="light"
            label="Heating units"
            disabled={!isSelectedSecondaryHeatingSourceUnitCountBased}
            helperText="Acceptable range: 1-1000"
          />
          <Field
            xs={6}
            type="select"
            id="farmRisk.building.residenceUsage"
            options={ruOptions}
            name={`${buildingPath}.residenceUsage`}
            mode="light"
            label="Residence Type"
          />
          <Field
            xs={6}
            type="string"
            id="farmRisk.building.constructionStyle"
            name={`${buildingPath}.constructionStyle`}
            mode="light"
            label="Areas under construction"
          />

          <Field
            type="select"
            id="farmRisk.building.roofOriginal"
            name={`${buildingPath}.roofOriginal`}
            options={roOptions}
            mode="light"
            label="Original Roof?"
            xs={3}
          />
          <Field
            xs={3}
            type="numeric"
            id="farmRisk.building.roofYear"
            name={`${buildingPath}.roofYear`}
            mode="light"
            label="Roof Year"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofType"
            options={rtOptions}
            name={`${buildingPath}.roofType`}
            mode="light"
            label="Roof type"
          />
          <Field
            xs={3}
            type="select"
            id="farmRisk.building.roofShape"
            options={rsOptions}
            name={`${buildingPath}.roofShape`}
            mode="light"
            label="Roof shape"
          />
          <Field
            xs={4}
            type="select"
            id="farmRisk.building.constructionType"
            name={`${buildingPath}.constructionType`}
            mode="light"
            label="Primary construction type"
            options={ctOptions}
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.latitude"
            name={`${buildingPath}.latitude`}
            mode="light"
            label="Latitude"
          />
          <Field
            xs={4}
            type="numeric"
            id="farmRisk.building.longitude"
            name={`${buildingPath}.longitude`}
            mode="light"
            label="Longitude"
          />
          <Grid item xs={12} className={classes.checkboxGridItem}>
            <Field
              type="checkbox"
              id="farmRisk.building.solarPanelsAttached"
              name={`${buildingPath}.solarPanelsAttached`}
              mode="light"
              label="Are there solar panels attached to this dwelling location?"
            />
          </Grid>
        </>
      )}

      {values.address && (
        <Grid item xs={12} alignItems="flex-start" className={classes.useApplicantAddressLabel}>
          <Field
            type="checkbox"
            id="farmRisk.useApplicantAddress"
            name={`${farmRiskPath}.useApplicantAddress`}
            mode="light"
            fast={false}
            label={
              <span>
                Use <b>{[values.address, values.city, values.state, values.zip].filter(Boolean).join(', ')}</b> as the
                address for this risk
              </span>
            }
          />
        </Grid>
      )}
      {!farmRisk?.useApplicantAddress && values.address && (
        <>
          <Field
            xs={6}
            type="address"
            id="farmRisk.address.address"
            name={`${riskAddressPath}.address`}
            mode="light"
            label="Address*"
          />
          <Field
            xs={6}
            type="string"
            id="farmRisk.address.address2"
            name={`${riskAddressPath}.address2`}
            mode="light"
            label="Address 2"
          />
          <Field
            xs={4}
            type="string"
            id="farmRisk.address.city"
            name={`${riskAddressPath}.city`}
            mode="light"
            label="City*"
          />
          <Field
            xs={4}
            type="string"
            id="farmRisk.address.state"
            name={`${riskAddressPath}.state`}
            mode="light"
            label="State*"
          />
          <Field
            xs={4}
            type="string"
            id="farmRisk.address.zip"
            name={`${riskAddressPath}.zip`}
            mode="light"
            label="Zip Code*"
          />
        </>
      )}
    </Grid>
  );
};

export default FO3RiskDetail;
